import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@krea/shared/design-system/Drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@krea/shared/design-system/Tooltip";
import { cn } from "@krea/shared/utils";
import { isMobile } from "@krea/shared/utils/isMobile";

export interface IResponsiveTooltipProps {
  /** When interactive === false, the element that opens the tooltip/drawer (hover on desktop, click on mobile). Required when interactive is false. */
  trigger?: ReactNode;
  /** When interactive === false: tooltip/drawer content. When interactive === true: the main interactive element (e.g. button); tooltip/drawer content then comes from content. */
  children: ReactNode;
  /** Default false. When true: desktop = only children with tooltip on hover (no ? icon). Mobile = children + ? icon; ? icon is click trigger for bottom drawer. */
  interactive?: boolean;
  /** Optional class name applied to the tooltip content (desktop) and bottom drawer body (mobile). */
  className?: string;
  /** Placement of the content. Default: "top". */
  side?: "top" | "right" | "bottom" | "left";
  /** Horizontal alignment (tooltip only). Default: "center". */
  align?: "start" | "center" | "end";
  /** Offset in pixels from the trigger (tooltip only). Default: 4. */
  sideOffset?: number;
  /** When true, render only the trigger (or only children when interactive); no tooltip or dialog. */
  hidden?: boolean;
  /** Default true. When true, tooltip/drawer stack above modals/drawers (higher z-index). */
  elevatedStacking?: boolean;
  /** When true, render only on desktop (hover) */
  desktopOnly?: boolean;
  /** Portal container element. Defaults to document.body. Pass the widget root to keep portaled content inside scoped styles. */
  portalContainer?: HTMLElement | null;
  /**
   * vaul snap points forwarded to Drawer.Root on mobile.
   * E.g. [0.4, 1] opens at 40% height; drag-up expands to full screen.
   * Omit for auto-height (default — fits content).
   */
  snapPoints?: (number | string)[];
}

const ELEVATED_Z_CONTENT = "z-500001";
const ELEVATED_Z_OVERLAY = "z-500000";

/**
 * Shows content in a tooltip on desktop (hover) and in a full-width bottom drawer on mobile (click).
 * Uses 768px as the breakpoint (matches Tailwind md).
 * When interactive: desktop = tooltip on children only (no ? icon); mobile = children + ? icon, ? icon opens drawer.
 */
export const ResponsiveTooltip = ({
  trigger,
  children,
  interactive = false,
  className,
  side = "top",
  align = "center",
  sideOffset = 4,
  hidden = false,
  elevatedStacking = true,
  desktopOnly = false,
  portalContainer,
  snapPoints,
}: IResponsiveTooltipProps) => {
  const isMobileCheck = isMobile();

  const mergedTooltipClassName = cn(
    className,
    elevatedStacking && ELEVATED_Z_CONTENT,
  );
  const drawerContentClassName = cn(
    className,
    elevatedStacking && ELEVATED_Z_CONTENT,
    "w-full max-w-[100vw]",
  );
  const drawerOverlayClassName = elevatedStacking
    ? ELEVATED_Z_OVERLAY
    : undefined;

  if (hidden || (isMobileCheck && desktopOnly)) {
    return trigger;
  }

  if (isMobileCheck && !desktopOnly) {
    if (interactive) {
      return (
        <Drawer snapPoints={snapPoints}>
          <div className="flex items-center">
            {trigger}
            <DrawerTrigger asChild>
              <span className="inline-flex cursor-pointer items-center justify-center rounded-full text-gray-600 hover:text-gray-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                <HelpCircle className="size-4" />
              </span>
            </DrawerTrigger>
          </div>
          <DrawerContent
            className={drawerContentClassName}
            overlayClassName={drawerOverlayClassName}
          >
            {children}
          </DrawerContent>
        </Drawer>
      );
    }

    return (
      <Drawer snapPoints={snapPoints}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          className={drawerContentClassName}
          overlayClassName={drawerOverlayClassName}
        >
          {children}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={mergedTooltipClassName}
          container={portalContainer}
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
